"use client"

/**
 * Selection invitation into Ask Leo. Stem uses the existing rich-text
 * BubbleMenu. Option rows raise the same AskLeoButton beside the span.
 * Click spends the Ask Leo turn. Do not open LeoAssistBar.
 */

import * as React from "react"
import { createPortal } from "react-dom"

import { AskLeoButton } from "@/components/ask-leo-button"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover"
import { getFieldSelectionRect } from "@/lib/textarea-selection-rect"

const SELECTION_SETTLE_MS = 180

function EnhanceSelectionButton({
  label,
  onAsk,
  floating = false,
}: {
  label: string
  onAsk: () => void
  floating?: boolean
}) {
  return (
    <span
      className="inline-flex"
      onMouseDown={(event) => event.preventDefault()}
    >
      <AskLeoButton
        size="sm"
        iconOnly
        variant={floating ? "outline" : "ghost"}
        label="Ask Leo"
        ariaLabel={label}
        tooltipLabel={label}
        showShortcut={false}
        animatedStar={false}
        introduceOnMount={floating}
        className={
          floating
            ? "bg-popover shadow-md dark:bg-popover dark:hover:bg-accent"
            : undefined
        }
        onClick={onAsk}
      />
    </span>
  )
}

function ensureAskMount(popover: HTMLElement): HTMLElement {
  let mount = popover.querySelector<HTMLElement>("[data-composer-selection-ask]")
  if (mount) return mount

  const divider = document.createElement("span")
  divider.className = "mx-1 h-5 w-px bg-border"
  divider.setAttribute("aria-hidden", "true")
  mount = document.createElement("span")
  mount.dataset.composerSelectionAsk = ""
  mount.className = "inline-flex items-center"
  popover.append(divider, mount)
  return mount
}

function selectedTextIn(root: HTMLElement): string {
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return ""
  }
  const range = selection.getRangeAt(0)
  if (!root.contains(range.commonAncestorContainer)) return ""
  return selection.toString().replace(/\s+/g, " ").trim()
}

export function ComposerRichTextSelectionAsk({
  rootRef,
  disabled,
  onAsk,
}: {
  rootRef: React.RefObject<HTMLElement | null>
  disabled?: boolean
  onAsk: (text: string) => void
}) {
  const [host, setHost] = React.useState<HTMLElement | null>(null)
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null)
  const [text, setText] = React.useState("")

  React.useEffect(() => {
    if (disabled) return

    let timer = 0

    function settle() {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        const root = rootRef.current
        const selected = root ? selectedTextIn(root) : ""
        if (!selected) {
          setHost(null)
          setAnchorRect(null)
          setText("")
          return
        }
        const popover = document.querySelector<HTMLElement>(
          "[data-rich-text-editor-popover]",
        )
        const range = window.getSelection()?.getRangeAt(0)
        setHost(popover ? ensureAskMount(popover) : null)
        setAnchorRect(popover ? null : range?.getBoundingClientRect() ?? null)
        setText(selected)
      }, SELECTION_SETTLE_MS)
    }

    document.addEventListener("selectionchange", settle)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener("selectionchange", settle)
    }
  }, [disabled, rootRef])

  if (disabled || !text) return null

  if (host) {
    return createPortal(
      <EnhanceSelectionButton
        label="Ask Leo about this selection"
        onAsk={() => onAsk(text)}
      />,
      host,
    )
  }

  if (!anchorRect || anchorRect.width === 0) return null

  return (
    <Popover open onOpenChange={() => undefined} modal={false}>
      <PopoverAnchor asChild>
        <span
          aria-hidden
          className="pointer-events-none fixed"
          style={{
            left: anchorRect.left,
            top: anchorRect.top,
            width: anchorRect.width,
            height: anchorRect.height,
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-auto border-0 bg-transparent p-0 shadow-none"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <EnhanceSelectionButton
          floating
          label="Ask Leo about this selection"
          onAsk={() => onAsk(text)}
        />
      </PopoverContent>
    </Popover>
  )
}

export function ComposerFieldSelectionAsk({
  inputRef,
  disabled,
  onAsk,
  ariaLabel = "Ask Leo about this selection",
}: {
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>
  disabled?: boolean
  onAsk: (text: string) => void
  ariaLabel?: string
}) {
  const [chipVisible, setChipVisible] = React.useState(false)
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null)
  const [text, setText] = React.useState("")

  const readSelection = React.useCallback(() => {
    const el = inputRef.current
    if (!el) return null
    const start = el.selectionStart ?? 0
    const end = el.selectionEnd ?? 0
    if (start === end) return null
    const next = el.value.slice(start, end).replace(/\s+/g, " ").trim()
    if (!next) return null
    return { start, end, text: next }
  }, [inputRef])

  React.useEffect(() => {
    const el = inputRef.current
    if (!el || disabled) return

    let timer = 0

    function settle() {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        const next = readSelection()
        const field = inputRef.current
        if (!next || !field) {
          setChipVisible(false)
          setText("")
          return
        }
        setText(next.text)
        setAnchorRect(getFieldSelectionRect(field, next.start, next.end))
        setChipVisible(true)
      }, SELECTION_SETTLE_MS)
    }

    el.addEventListener("select", settle)
    el.addEventListener("mouseup", settle)
    el.addEventListener("keyup", settle)

    return () => {
      window.clearTimeout(timer)
      el.removeEventListener("select", settle)
      el.removeEventListener("mouseup", settle)
      el.removeEventListener("keyup", settle)
    }
  }, [disabled, inputRef, readSelection])

  React.useEffect(() => {
    if (!chipVisible) return
    function reposition() {
      const next = readSelection()
      const field = inputRef.current
      if (!next || !field) return
      setAnchorRect(getFieldSelectionRect(field, next.start, next.end))
    }
    window.addEventListener("scroll", reposition, true)
    window.addEventListener("resize", reposition)
    return () => {
      window.removeEventListener("scroll", reposition, true)
      window.removeEventListener("resize", reposition)
    }
  }, [chipVisible, inputRef, readSelection])

  if (disabled) return null

  return (
    <Popover open={chipVisible} onOpenChange={setChipVisible} modal={false}>
      {anchorRect ? (
        <PopoverAnchor asChild>
          <span
            aria-hidden
            className="pointer-events-none fixed"
            style={{
              left: anchorRect.left,
              top: anchorRect.top,
              width: anchorRect.width,
              height: anchorRect.height,
            }}
          />
        </PopoverAnchor>
      ) : null}
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-auto border-0 bg-transparent p-0 shadow-none"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <EnhanceSelectionButton
          floating
          label={ariaLabel}
          onAsk={() => {
            if (!text) return
            setChipVisible(false)
            onAsk(text)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
